/**
 * Opt-in trace buffer for the reading-tracking pipeline.
 *
 * The tracking chain spans two windows (this reader iframe and the host
 * page) and its failure modes are timing-sensitive: a network capture only
 * shows what survived every stage, so diagnosing a lost or phantom event
 * needs the intermediate decisions (visibility checks, timers, emissions)
 * with timestamps. This module records those decisions when — and only
 * when — debugging is explicitly enabled:
 *
 * - `?plaDebug=1` on the reader URL (the host propagates its own flag to
 *   the iframe src), or
 * - `localStorage['pla:tracking-debug'] = '1'` in this window.
 *
 * Disabled (the default) every call is a cheap early return, so call sites
 * can trace unconditionally. Enabled, entries accumulate in
 * `window.__plaTrace` (bounded ring) and are mirrored to the parent window
 * via postMessage, where the host merges them with its own entries into a
 * single cross-window timeline.
 */

const QUERY_FLAG = 'plaDebug';
const STORAGE_KEY = 'pla:tracking-debug';
const MAX_BUFFER_ENTRIES = 3000;

/** postMessage envelope marker the host-side collector listens for. */
export const TRACE_MESSAGE_TYPE = 'pla-tracking-trace';

export interface TrackingTraceEntry {
    /** Wall-clock ms (Date.now) — aligns entries across windows. */
    at: number;
    /** Monotonic ms within this window (performance.now, rounded). */
    t: number;
    source: 'volpe';
    cat: string;
    [detail: string]: unknown;
}

interface TraceWindow extends Window {
    __plaTrace?: TrackingTraceEntry[];
}

let cachedEnabled: boolean | null = null;
let cachedParentOrigin: string | null = null;

function readFlag(): boolean {
    try {
        const fromQuery = new URLSearchParams(window.location.search).get(QUERY_FLAG);
        if (fromQuery !== null) return fromQuery === '1' || fromQuery === 'true';
    } catch {
        // Unparseable search string: fall through to storage.
    }

    try {
        return window.localStorage?.getItem(STORAGE_KEY) === '1';
    } catch {
        // Storage can throw in third-party-iframe/private contexts; treat as off.
        return false;
    }
}

export function isTrackingDebugEnabled(): boolean {
    if (cachedEnabled === null) {
        cachedEnabled = readFlag();
        if (cachedEnabled) {
            // eslint-disable-next-line no-console
            console.info(
                '[pla-trace] tracking debug enabled — entries at window.__plaTrace, mirrored to the host frame'
            );
        }
    }
    return cachedEnabled;
}

/** Forget the memoized flag/origin so tests can flip the environment. */
export function resetTrackingDebugCache(): void {
    cachedEnabled = null;
    cachedParentOrigin = null;
}

/**
 * The host page's origin, so the mirror messages are not readable by
 * arbitrary ancestors. The referrer of an embedded reader is its host page;
 * standalone (no referrer / opaque) falls back to same-window only anyway
 * because there is no distinct parent.
 */
function parentOrigin(): string {
    if (cachedParentOrigin === null) {
        try {
            cachedParentOrigin = document.referrer ? new URL(document.referrer).origin : '*';
        } catch {
            cachedParentOrigin = '*';
        }
    }
    return cachedParentOrigin;
}

/**
 * Record one trace entry. No-op unless tracking debug is enabled — data
 * that is expensive to assemble should be gated at the call site with
 * `isTrackingDebugEnabled()` instead of precomputed.
 */
export function trackingTrace(cat: string, data: Record<string, unknown> = {}): void {
    if (!isTrackingDebugEnabled()) return;

    const entry: TrackingTraceEntry = {
        at: Date.now(),
        t: Math.round(performance.now()),
        source: 'volpe',
        cat,
        ...data,
    };

    const w = window as TraceWindow;
    if (!w.__plaTrace) w.__plaTrace = [];
    w.__plaTrace.push(entry);
    if (w.__plaTrace.length > MAX_BUFFER_ENTRIES) w.__plaTrace.shift();

    if (window.parent && window.parent !== window) {
        try {
            window.parent.postMessage({ type: TRACE_MESSAGE_TYPE, entry }, parentOrigin());
        } catch {
            // A host that can't receive the mirror still leaves the local buffer usable.
        }
    }
}
